import React, { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";

const Reports = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [voterStats, setVoterStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingLoad, setPendingLoad] = useState(false);
  const [voterLoad, setVoterLoad] = useState(false);
  const [budgetStats, setBudgetStats] = useState(null);
  const [budgetLoad, setBudgetLoad] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPri, setFilterPri] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ── Fetch Grievance Summary ───────────────────────────────
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/grievances/", {
        params: { page: 1, per_page: 500 },
      });
      const list = res.data.data.grievances || [];

      const byStatus = {},
        byPriority = {},
        byCategory = {},
        byLevel = {};
      let resolved = 0,
        totalDays = 0,
        countDays = 0;

      list.forEach((g) => {
        byStatus[g.current_status] = (byStatus[g.current_status] || 0) + 1;
        byPriority[g.priority] = (byPriority[g.priority] || 0) + 1;
        if (g.category)
          byCategory[g.category] = (byCategory[g.category] || 0) + 1;
        byLevel[`Level ${g.current_level}`] =
          (byLevel[`Level ${g.current_level}`] || 0) + 1;
        if (["Resolved", "Closed"].includes(g.current_status)) resolved++;
        if (g.resolved_at && g.created_at) {
          totalDays +=
            (new Date(g.resolved_at) - new Date(g.created_at)) /
            (1000 * 3600 * 24);
          countDays++;
        }
      });

      setSummary({
        total: list.length,
        resolved,
        pending: list.length - resolved,
        resolutionRate: list.length
          ? Math.round((resolved / list.length) * 100)
          : 0,
        avgDays: countDays ? Math.round(totalDays / countDays) : 0,
        byStatus,
        byPriority,
        byCategory,
        byLevel,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch Pending ─────────────────────────────────────────
  const fetchPending = useCallback(
    async (page = 1) => {
      setPendingLoad(true);
      try {
        const res = await API.get("/grievances/", {
          params: {
            page,
            per_page: 10,
            status: filterStatus || undefined,
            priority: filterPri || undefined,
          },
        });
        const all = res.data.data.grievances || [];
        setPending(
          filterStatus
            ? all
            : all.filter(
                (g) => !["Resolved", "Closed"].includes(g.current_status),
              ),
        );
        setTotalPages(res.data.data.pagination.total_pages);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setPendingLoad(false);
      }
    },
    [filterStatus, filterPri],
  );

  // ── Fetch Voter Stats ─────────────────────────────────────
  const fetchVoterStats = useCallback(async () => {
    setVoterLoad(true);
    try {
      // Fetch all voters (large batch)
      const res = await API.get("/voters/", {
        params: { page: 1, per_page: 500 },
      });
      const list = res.data.data.voters || [];
      const total = res.data.data.pagination.total;

      const byGender = {},
        byBooth = {},
        byAge = {
          "18-25": 0,
          "26-35": 0,
          "36-45": 0,
          "46-60": 0,
          "60+": 0,
          Unknown: 0,
        };

      list.forEach((v) => {
        // Gender
        const g = v.gender || "Unknown";
        byGender[g] = (byGender[g] || 0) + 1;

        // Booth
        const b = `Booth ${v.booth_no || "Unknown"}`;
        byBooth[b] = (byBooth[b] || 0) + 1;

        // Age Group
        const age = parseInt(v.age) || 0;
        if (!age) byAge["Unknown"]++;
        else if (age <= 25) byAge["18-25"]++;
        else if (age <= 35) byAge["26-35"]++;
        else if (age <= 45) byAge["36-45"]++;
        else if (age <= 60) byAge["46-60"]++;
        else byAge["60+"]++;
      });

      // Sort booth by number
      const sortedBooth = Object.entries(byBooth).sort((a, b) => {
        const na = parseInt(a[0].replace("Booth ", "")) || 0;
        const nb = parseInt(b[0].replace("Booth ", "")) || 0;
        return na - nb;
      });

      setVoterStats({
        total,
        byGender,
        byBooth: Object.fromEntries(sortedBooth),
        byAge,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setVoterLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, []);
  useEffect(() => {
    if (activeTab === "pending") fetchPending(1);
  }, [activeTab, filterStatus, filterPri]);
  useEffect(() => {
    if (activeTab === "voter") fetchVoterStats();
  }, [activeTab]);

  // ── Fetch Budget Stats ────────────────────────────────────
  const fetchBudgetStats = useCallback(async () => {
    setBudgetLoad(true);
    try {
      const res = await API.get("/development-projects/", {
        params: { page: 1, per_page: 500 },
      });
      const list = res.data.data.projects || [];

      const byStatus = {};
      const byDept = {};
      const byYear = {};
      const byCategory = {};

      let totalEstimated = 0;
      let totalTender = 0;
      let completed = 0;
      let inProgress = 0;

      list.forEach((p) => {
        const est = parseFloat(p.estimated_amount || 0);
        const ten = parseFloat(p.tender_amount || 0);
        totalEstimated += est;
        totalTender += ten;

        // By Status
        byStatus[p.status] = (byStatus[p.status] || 0) + 1;
        if (p.status === "Completed") completed++;
        if (p.status === "In Progress") inProgress++;

        // By Department
        if (p.department) {
          if (!byDept[p.department])
            byDept[p.department] = { count: 0, estimated: 0, tender: 0 };
          byDept[p.department].count++;
          byDept[p.department].estimated += est;
          byDept[p.department].tender += ten;
        }

        // By Financial Year
        if (p.financial_year) {
          if (!byYear[p.financial_year])
            byYear[p.financial_year] = { count: 0, estimated: 0, tender: 0 };
          byYear[p.financial_year].count++;
          byYear[p.financial_year].estimated += est;
          byYear[p.financial_year].tender += ten;
        }

        // By Category
        if (p.category) {
          if (!byCategory[p.category])
            byCategory[p.category] = { count: 0, estimated: 0 };
          byCategory[p.category].count++;
          byCategory[p.category].estimated += est;
        }
      });

      setBudgetStats({
        total: list.length,
        completed,
        inProgress,
        totalEstimated,
        totalTender,
        savings: totalEstimated - totalTender,
        byStatus,
        byDept,
        byYear,
        byCategory,
        projects: list,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setBudgetLoad(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "budget") fetchBudgetStats();
  }, [activeTab]);

  const statusColors = {
    New: { bg: "#dbeafe", color: "#1d4ed8" },
    InProgress: { bg: "#fef9c3", color: "#a16207" },
    OnHold: { bg: "#f3f4f6", color: "#374151" },
    Resolved: { bg: "#dcfce7", color: "#15803d" },
    Closed: { bg: "#f1f5f9", color: "#334155" },
    Escalated: { bg: "#fee2e2", color: "#b91c1c" },
  };
  const priorityColors = {
    LOW: "#22c55e",
    MEDIUM: "#f59e0b",
    HIGH: "#f97316",
    CRITICAL: "#ef4444",
  };
  const priorityBadge = {
    LOW: "bg-success-subtle text-success",
    MEDIUM: "bg-warning-subtle text-warning",
    HIGH: "bg-warning-subtle text-warning",
    CRITICAL: "bg-danger-subtle text-danger",
  };
  const genderColors = {
    M: "#3b82f6",
    F: "#ec4899",
    O: "#a855f7",
    Unknown: "#9ca3af",
  };
  const genderLabels = {
    M: "Male",
    F: "Female",
    O: "Other",
    Unknown: "Unknown",
  };
  const ageColors = [
    "#6366f1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#9ca3af",
  ];

  const formatCurrency = (v) =>
    v >= 10000000
      ? `₹${(v / 10000000).toFixed(2)} Cr`
      : v >= 100000
        ? `₹${(v / 100000).toFixed(2)} L`
        : `₹${v.toLocaleString("en-IN")}`;

  const tabs = [
    { key: "summary", icon: "bi-bar-chart-fill", label: "Grievance Summary" },
    { key: "pending", icon: "bi-clock-history", label: "Pending List" },
    { key: "voter", icon: "bi-people-fill", label: "Voter Summary" },
    { key: "budget", icon: "bi-currency-rupee", label: "Project Budget" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Reports</h4>
          <p className="text-muted small mb-0">Grievance and voter analysis</p>
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => window.print()}
        >
          <i className="bi bi-printer me-2"></i>Print Report
        </button>
      </div>

      {/* Tabs */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-2">
          <div className="d-flex gap-2 flex-wrap">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={`btn rounded-3 px-4 ${activeTab === t.key ? "text-white" : "btn-light"}`}
                style={
                  activeTab === t.key
                    ? { background: "linear-gradient(135deg,#1a3c5e,#2d6a4f)" }
                    : {}
                }
                onClick={() => setActiveTab(t.key)}
              >
                <i className={`bi ${t.icon} me-2`}></i>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── GRIEVANCE SUMMARY TAB ── */}
      {activeTab === "summary" &&
        (loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : (
          summary && (
            <div>
              {/* KPI Cards */}
              <div className="row g-3 mb-4">
                {[
                  {
                    label: "Total Grievances",
                    value: summary.total,
                    icon: "bi-chat-left-text-fill",
                    color: "#1d4ed8",
                    bg: "#dbeafe",
                  },
                  {
                    label: "Pending",
                    value: summary.pending,
                    icon: "bi-hourglass-split",
                    color: "#a16207",
                    bg: "#fef9c3",
                  },
                  {
                    label: "Resolved/Closed",
                    value: summary.resolved,
                    icon: "bi-check-circle-fill",
                    color: "#15803d",
                    bg: "#dcfce7",
                  },
                  {
                    label: "Resolution Rate",
                    value: `${summary.resolutionRate}%`,
                    icon: "bi-graph-up",
                    color: "#7c3aed",
                    bg: "#ede9fe",
                  },
                  {
                    label: "Avg Resolution",
                    value: `${summary.avgDays} days`,
                    icon: "bi-calendar-check",
                    color: "#0e7490",
                    bg: "#cffafe",
                  },
                  {
                    label: "Escalated",
                    value: summary.byStatus?.Escalated || 0,
                    icon: "bi-exclamation-triangle-fill",
                    color: "#b91c1c",
                    bg: "#fee2e2",
                  },
                ].map((kpi, i) => (
                  <div key={i} className="col-6 col-md-4 col-lg-2">
                    <div className="card border-0 rounded-4 shadow-sm h-100">
                      <div className="card-body p-3 text-center">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{ width: 48, height: 48, background: kpi.bg }}
                        >
                          <i
                            className={`bi ${kpi.icon} fs-5`}
                            style={{ color: kpi.color }}
                          ></i>
                        </div>
                        <div
                          className="fw-bold fs-4"
                          style={{ color: kpi.color }}
                        >
                          {kpi.value}
                        </div>
                        <div className="text-muted small">{kpi.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row g-3 mb-3">
                {/* By Status */}
                <div className="col-12 col-lg-6">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-pie-chart me-2 text-primary"></i>By
                        Status
                      </h6>
                      {Object.entries(summary.byStatus || {}).map(
                        ([status, count]) => {
                          const pct = Math.round(
                            (count / (summary.total || 1)) * 100,
                          );
                          const c = statusColors[status] || {
                            bg: "#f3f4f6",
                            color: "#374151",
                          };
                          return (
                            <div key={status} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span
                                  className="small fw-medium"
                                  style={{ color: c.color }}
                                >
                                  {status}
                                </span>
                                <div className="d-flex gap-2">
                                  <span className="fw-bold small">{count}</span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                              <div
                                className="rounded-pill"
                                style={{ height: 10, background: "#f0f2f5" }}
                              >
                                <div
                                  className="rounded-pill h-100"
                                  style={{
                                    width: `${pct}%`,
                                    background: c.color,
                                    transition: "width 1s",
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>

                {/* By Priority */}
                <div className="col-12 col-lg-6">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-flag-fill me-2 text-danger"></i>By
                        Priority
                      </h6>
                      {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((p) => {
                        const count = summary.byPriority?.[p] || 0;
                        const pct = Math.round(
                          (count / (summary.total || 1)) * 100,
                        );
                        return (
                          <div key={p} className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="rounded-circle"
                                  style={{
                                    width: 10,
                                    height: 10,
                                    background: priorityColors[p],
                                  }}
                                ></div>
                                <span className="small fw-medium">{p}</span>
                              </div>
                              <div className="d-flex gap-2">
                                <span className="fw-bold small">{count}</span>
                                <span
                                  className="text-muted"
                                  style={{ fontSize: "11px" }}
                                >
                                  {pct}%
                                </span>
                              </div>
                            </div>
                            <div
                              className="rounded-pill"
                              style={{ height: 10, background: "#f0f2f5" }}
                            >
                              <div
                                className="rounded-pill h-100"
                                style={{
                                  width: `${pct}%`,
                                  background: priorityColors[p],
                                  transition: "width 1s",
                                }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                {/* By Category */}
                <div className="col-12 col-lg-6">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-tags-fill me-2 text-info"></i>By
                        Category
                      </h6>
                      {Object.entries(summary.byCategory || {}).length === 0 ? (
                        <div className="text-muted small text-center py-3">
                          No category data
                        </div>
                      ) : (
                        Object.entries(summary.byCategory || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([cat, count]) => {
                            const pct = Math.round(
                              (count / (summary.total || 1)) * 100,
                            );
                            return (
                              <div key={cat} className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                  <span className="small fw-medium">{cat}</span>
                                  <div className="d-flex gap-2">
                                    <span className="fw-bold small">
                                      {count}
                                    </span>
                                    <span
                                      className="text-muted"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {pct}%
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className="rounded-pill"
                                  style={{ height: 8, background: "#f0f2f5" }}
                                >
                                  <div
                                    className="rounded-pill h-100"
                                    style={{
                                      width: `${pct}%`,
                                      background:
                                        "linear-gradient(135deg,#1a3c5e,#2d6a4f)",
                                      transition: "width 1s",
                                    }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </div>
                </div>

                {/* By Level */}
                <div className="col-12 col-lg-6">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-layers-fill me-2 text-warning"></i>
                        By Escalation Level
                      </h6>
                      {Object.entries(summary.byLevel || {}).map(
                        ([level, count]) => {
                          const pct = Math.round(
                            (count / (summary.total || 1)) * 100,
                          );
                          const lvlCol = ["#0ea5e9", "#f59e0b", "#ef4444"];
                          const col =
                            lvlCol[parseInt(level.split(" ")[1]) - 1] ||
                            "#6b7280";
                          return (
                            <div key={level} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <span
                                  className="small fw-medium"
                                  style={{ color: col }}
                                >
                                  {level}
                                </span>
                                <div className="d-flex gap-2">
                                  <span className="fw-bold small">{count}</span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                              <div
                                className="rounded-pill"
                                style={{ height: 10, background: "#f0f2f5" }}
                              >
                                <div
                                  className="rounded-pill h-100"
                                  style={{
                                    width: `${pct}%`,
                                    background: col,
                                    transition: "width 1s",
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        },
                      )}
                      <div
                        className="mt-3 p-3 rounded-3"
                        style={{ background: "#f8fafc" }}
                      >
                        <div className="row text-center g-2">
                          <div className="col-4">
                            <div className="fw-bold text-primary fs-5">
                              {summary.total}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Total
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="fw-bold text-success fs-5">
                              {summary.resolved}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Resolved
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="fw-bold text-warning fs-5">
                              {summary.pending}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Pending
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ))}

      {/* ── PENDING LIST TAB ── */}
      {activeTab === "pending" && (
        <div>
          <div className="card border-0 shadow-sm rounded-4 mb-3">
            <div className="card-body p-3">
              <div className="row g-2 align-items-end">
                <div className="col-6 col-md-3">
                  <label className="form-label small text-muted mb-1">
                    Status
                  </label>
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All (excl. Resolved/Closed)</option>
                    {[
                      "New",
                      "InProgress",
                      "OnHold",
                      "Escalated",
                      "Resolved",
                      "Closed",
                    ].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-muted mb-1">
                    Priority
                  </label>
                  <select
                    className="form-select"
                    value={filterPri}
                    onChange={(e) => setFilterPri(e.target.value)}
                  >
                    <option value="">All Priorities</option>
                    {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <button
                    className="btn btn-outline-secondary w-100 mt-3"
                    onClick={() => {
                      setFilterStatus("");
                      setFilterPri("");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-0">
              {pendingLoad ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : pending.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-check-circle fs-1 d-block mb-2 text-success"></i>
                  No pending grievances! All resolved ✅
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="px-4 py-3 small text-muted fw-semibold">
                          #
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Ticket
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Title
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Reporter
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Category
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Priority
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Status
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Assigned To
                        </th>
                        <th className="py-3 small text-muted fw-semibold">
                          Days Pending
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((g, idx) => {
                        const days = Math.floor(
                          (new Date() - new Date(g.created_at)) /
                            (1000 * 3600 * 24),
                        );
                        const c = statusColors[g.current_status] || {
                          bg: "#f3f4f6",
                          color: "#374151",
                        };
                        return (
                          <tr
                            key={g.grievance_id}
                            style={days > 7 ? { background: "#fff8f8" } : {}}
                          >
                            <td className="px-4 text-muted small">{idx + 1}</td>
                            <td>
                              <span className="badge bg-light text-dark border small fw-normal">
                                {g.ticket_no}
                              </span>
                            </td>
                            <td style={{ maxWidth: "180px" }}>
                              <div className="fw-semibold small text-truncate">
                                {g.title}
                              </div>
                            </td>
                            <td>
                              <div className="small">
                                {g.reporter_name || "—"}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                {g.reporter_mobile || ""}
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border small">
                                {g.category || "—"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge small ${priorityBadge[g.priority] || ""}`}
                              >
                                {g.priority}
                              </span>
                            </td>
                            <td>
                              <span
                                className="badge small rounded-pill"
                                style={{ background: c.bg, color: c.color }}
                              >
                                {g.current_status}
                              </span>
                            </td>
                            <td className="small">{g.assignee_name || "—"}</td>
                            <td>
                              <span
                                className={`badge rounded-pill small ${
                                  days > 14
                                    ? "bg-danger text-white"
                                    : days > 7
                                      ? "bg-warning text-dark"
                                      : "bg-success-subtle text-success"
                                }`}
                              >
                                {days} days
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
                <span className="text-muted small">
                  Page {currentPage} of {totalPages}
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchPending(currentPage - 1)}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      const page =
                        currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                      if (page > totalPages) return null;
                      return (
                        <li
                          key={page}
                          className={`page-item ${currentPage === page ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => fetchPending(page)}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    })}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchPending(currentPage + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── VOTER SUMMARY TAB ── */}
      {activeTab === "voter" &&
        (voterLoad ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : (
          voterStats && (
            <div>
              {/* Total Voters KPI */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-md-4">
                  <div
                    className="card border-0 rounded-4 shadow-sm"
                    style={{
                      background: "linear-gradient(135deg,#1a3c5e,#2563eb)",
                    }}
                  >
                    <div className="card-body p-4 text-white">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: 56,
                            height: 56,
                            background: "rgba(255,255,255,0.2)",
                          }}
                        >
                          <i className="bi bi-people-fill fs-3"></i>
                        </div>
                        <div>
                          <div className="text-white text-opacity-75 small">
                            Total Voters
                          </div>
                          <div className="fw-bold" style={{ fontSize: "32px" }}>
                            {(voterStats.total || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gender KPIs */}
                {Object.entries(voterStats.byGender || {}).map(([g, count]) => (
                  <div key={g} className="col-6 col-md-2">
                    <div className="card border-0 rounded-4 shadow-sm h-100">
                      <div className="card-body p-3 text-center">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{
                            width: 44,
                            height: 44,
                            background: `${genderColors[g]}22`,
                          }}
                        >
                          <i
                            className={`bi ${g === "M" ? "bi-gender-male" : g === "F" ? "bi-gender-female" : "bi-person"} fs-5`}
                            style={{ color: genderColors[g] }}
                          ></i>
                        </div>
                        <div
                          className="fw-bold fs-4"
                          style={{ color: genderColors[g] }}
                        >
                          {count}
                        </div>
                        <div className="text-muted small">
                          {genderLabels[g] || g}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px" }}
                        >
                          {voterStats.total
                            ? Math.round((count / voterStats.total) * 100)
                            : 0}
                          %
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row g-3 mb-3">
                {/* Gender Chart */}
                <div className="col-12 col-md-5">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-gender-ambiguous me-2 text-primary"></i>
                        Gender Distribution
                      </h6>
                      {Object.entries(voterStats.byGender || {}).map(
                        ([g, count]) => {
                          const pct = voterStats.total
                            ? Math.round((count / voterStats.total) * 100)
                            : 0;
                          return (
                            <div key={g} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      background: genderColors[g],
                                    }}
                                  ></div>
                                  <span className="small fw-medium">
                                    {genderLabels[g] || g}
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  <span className="fw-bold small">
                                    {count.toLocaleString("en-IN")}
                                  </span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                              <div
                                className="rounded-pill"
                                style={{ height: 12, background: "#f0f2f5" }}
                              >
                                <div
                                  className="rounded-pill h-100"
                                  style={{
                                    width: `${pct}%`,
                                    background: genderColors[g],
                                    transition: "width 1s",
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>

                {/* Age Group Chart */}
                <div className="col-12 col-md-7">
                  <div className="card border-0 rounded-4 shadow-sm">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-person-lines-fill me-2 text-success"></i>
                        Age Group Distribution
                      </h6>
                      {Object.entries(voterStats.byAge || {}).map(
                        ([ageGrp, count], i) => {
                          const pct = voterStats.total
                            ? Math.round((count / voterStats.total) * 100)
                            : 0;
                          return (
                            <div key={ageGrp} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      background: ageColors[i],
                                    }}
                                  ></div>
                                  <span className="small fw-medium">
                                    {ageGrp} yrs
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  <span className="fw-bold small">
                                    {count.toLocaleString("en-IN")}
                                  </span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                              <div
                                className="rounded-pill"
                                style={{ height: 12, background: "#f0f2f5" }}
                              >
                                <div
                                  className="rounded-pill h-100"
                                  style={{
                                    width: `${pct}%`,
                                    background: ageColors[i],
                                    transition: "width 1s",
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Booth-wise Table */}
              <div className="card border-0 rounded-4 shadow-sm">
                <div className="card-body p-4">
                  <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                    <i className="bi bi-geo-alt-fill me-2 text-danger"></i>
                    Booth-wise Voter Count
                    <span
                      className="text-muted fw-normal ms-2"
                      style={{ fontSize: "12px" }}
                    >
                      (from loaded data)
                    </span>
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="px-4 py-3 small text-muted fw-semibold">
                            #
                          </th>
                          <th className="py-3 small text-muted fw-semibold">
                            Booth
                          </th>
                          <th className="py-3 small text-muted fw-semibold">
                            Voters
                          </th>
                          <th className="py-3 small text-muted fw-semibold">
                            Share
                          </th>
                          <th
                            className="py-3 small text-muted fw-semibold"
                            style={{ minWidth: "200px" }}
                          >
                            Distribution
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(voterStats.byBooth || {}).map(
                          ([booth, count], idx) => {
                            const pct = voterStats.total
                              ? Math.round((count / voterStats.total) * 100)
                              : 0;
                            return (
                              <tr key={booth}>
                                <td className="px-4 text-muted small">
                                  {idx + 1}
                                </td>
                                <td>
                                  <span className="badge bg-primary-subtle text-primary">
                                    {booth}
                                  </span>
                                </td>
                                <td className="fw-bold">
                                  {count.toLocaleString("en-IN")}
                                </td>
                                <td className="small text-muted">{pct}%</td>
                                <td>
                                  <div
                                    className="rounded-pill"
                                    style={{ height: 8, background: "#f0f2f5" }}
                                  >
                                    <div
                                      className="rounded-pill h-100"
                                      style={{
                                        width: `${pct}%`,
                                        background:
                                          "linear-gradient(135deg,#1a3c5e,#2d6a4f)",
                                        transition: "width 1s",
                                      }}
                                    ></div>
                                  </div>
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                      <tfoot className="bg-light">
                        <tr>
                          <td colSpan={2} className="px-4 fw-bold small">
                            Total (loaded)
                          </td>
                          <td className="fw-bold">
                            {Object.values(voterStats.byBooth || {})
                              .reduce((a, b) => a + b, 0)
                              .toLocaleString("en-IN")}
                          </td>
                          <td colSpan={2} className="text-muted small">
                            of {voterStats.total.toLocaleString("en-IN")} total
                            voters
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )
        ))}

      {/* ── PROJECT BUDGET TAB ── */}
      {activeTab === "budget" &&
        (budgetLoad ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
          </div>
        ) : (
          budgetStats && (
            <div>
              {/* KPI Cards */}
              <div className="row g-3 mb-4">
                {[
                  {
                    label: "Total Projects",
                    value: budgetStats.total,
                    icon: "bi-building-fill",
                    color: "#1d4ed8",
                    bg: "#dbeafe",
                  },
                  {
                    label: "Total Estimated",
                    value: formatCurrency(budgetStats.totalEstimated),
                    icon: "bi-cash-stack",
                    color: "#15803d",
                    bg: "#dcfce7",
                  },
                  {
                    label: "Total Tender",
                    value: formatCurrency(budgetStats.totalTender),
                    icon: "bi-receipt",
                    color: "#a16207",
                    bg: "#fef9c3",
                  },
                  {
                    label: "Savings",
                    value: formatCurrency(Math.abs(budgetStats.savings)),
                    icon: "bi-piggy-bank-fill",
                    color: "#7c3aed",
                    bg: "#ede9fe",
                  },
                  {
                    label: "Completed",
                    value: budgetStats.completed,
                    icon: "bi-check-circle-fill",
                    color: "#15803d",
                    bg: "#dcfce7",
                  },
                  {
                    label: "In Progress",
                    value: budgetStats.inProgress,
                    icon: "bi-gear-fill",
                    color: "#f97316",
                    bg: "#ffedd5",
                  },
                ].map((kpi, i) => (
                  <div key={i} className="col-6 col-md-4 col-lg-2">
                    <div className="card border-0 rounded-4 shadow-sm h-100">
                      <div className="card-body p-3 text-center">
                        <div
                          className="rounded-3 d-flex align-items-center
                        justify-content-center mx-auto mb-2"
                          style={{ width: 48, height: 48, background: kpi.bg }}
                        >
                          <i
                            className={`bi ${kpi.icon} fs-5`}
                            style={{ color: kpi.color }}
                          ></i>
                        </div>
                        <div
                          className="fw-bold fs-5"
                          style={{ color: kpi.color }}
                        >
                          {kpi.value}
                        </div>
                        <div className="text-muted small">{kpi.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* By Status + By Year */}
              <div className="row g-3 mb-3">
                {/* Project Status */}
                <div className="col-12 col-lg-5">
                  <div className="card border-0 rounded-4 shadow-sm h-100">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-pie-chart me-2 text-primary"></i>
                        Projects by Status
                      </h6>
                      {Object.entries(budgetStats.byStatus || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([status, count]) => {
                          const pct = Math.round(
                            (count / (budgetStats.total || 1)) * 100,
                          );
                          const statusCol = {
                            Planned: "#6366f1",
                            Sanctioned: "#0ea5e9",
                            Tendered: "#8b5cf6",
                            "Work Started": "#f59e0b",
                            "In Progress": "#f97316",
                            "On Hold": "#ef4444",
                            Completed: "#22c55e",
                            Cancelled: "#6b7280",
                          };
                          const col = statusCol[status] || "#6b7280";
                          return (
                            <div key={status} className="mb-3">
                              <div className="d-flex justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      background: col,
                                    }}
                                  ></div>
                                  <span className="small fw-medium">
                                    {status}
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  <span className="fw-bold small">{count}</span>
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {pct}%
                                  </span>
                                </div>
                              </div>
                              <div
                                className="rounded-pill"
                                style={{ height: 8, background: "#f0f2f5" }}
                              >
                                <div
                                  className="rounded-pill h-100"
                                  style={{
                                    width: `${pct}%`,
                                    background: col,
                                    transition: "width 1s",
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* By Financial Year */}
                <div className="col-12 col-lg-7">
                  <div className="card border-0 rounded-4 shadow-sm h-100">
                    <div className="card-body p-4">
                      <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                        <i className="bi bi-calendar3 me-2 text-warning"></i>
                        Budget by Financial Year
                      </h6>
                      {Object.entries(budgetStats.byYear || {}).length === 0 ? (
                        <div className="text-muted small text-center py-3">
                          No year data
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle small mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="py-2 small text-muted fw-semibold">
                                  Year
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Projects
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Estimated
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Tender
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Saving
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(budgetStats.byYear || {})
                                .sort((a, b) => a[0].localeCompare(b[0]))
                                .map(([year, data]) => (
                                  <tr key={year}>
                                    <td className="fw-medium">{year}</td>
                                    <td>
                                      <span className="badge bg-primary-subtle text-primary">
                                        {data.count}
                                      </span>
                                    </td>
                                    <td className="text-success fw-medium">
                                      {formatCurrency(data.estimated)}
                                    </td>
                                    <td className="text-warning fw-medium">
                                      {formatCurrency(data.tender)}
                                    </td>
                                    <td>
                                      <span
                                        className={
                                          data.estimated > data.tender
                                            ? "text-success fw-medium"
                                            : "text-danger fw-medium"
                                        }
                                      >
                                        {formatCurrency(
                                          Math.abs(
                                            data.estimated - data.tender,
                                          ),
                                        )}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-light">
                              <tr>
                                <td className="fw-bold">Total</td>
                                <td className="fw-bold">{budgetStats.total}</td>
                                <td className="fw-bold text-success">
                                  {formatCurrency(budgetStats.totalEstimated)}
                                </td>
                                <td className="fw-bold text-warning">
                                  {formatCurrency(budgetStats.totalTender)}
                                </td>
                                <td className="fw-bold text-success">
                                  {formatCurrency(
                                    Math.abs(budgetStats.savings),
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* By Department */}
              {Object.entries(budgetStats.byDept || {}).length > 0 && (
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <div className="card border-0 rounded-4 shadow-sm">
                      <div className="card-body p-4">
                        <h6
                          className="fw-bold mb-3"
                          style={{ color: "#1a3c5e" }}
                        >
                          <i className="bi bi-building me-2 text-success"></i>
                          Budget by Department
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-hover align-middle small mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="px-3 py-2 small text-muted fw-semibold">
                                  #
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Department
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Projects
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Estimated
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Tender
                                </th>
                                <th className="py-2 small text-muted fw-semibold">
                                  Share of Budget
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(budgetStats.byDept || {})
                                .sort((a, b) => b[1].estimated - a[1].estimated)
                                .map(([dept, data], idx) => {
                                  const pct = Math.round(
                                    (data.estimated /
                                      (budgetStats.totalEstimated || 1)) *
                                      100,
                                  );
                                  return (
                                    <tr key={dept}>
                                      <td className="px-3 text-muted">
                                        {idx + 1}
                                      </td>
                                      <td className="fw-medium">{dept}</td>
                                      <td>
                                        <span className="badge bg-primary-subtle text-primary">
                                          {data.count}
                                        </span>
                                      </td>
                                      <td className="text-success fw-medium">
                                        {formatCurrency(data.estimated)}
                                      </td>
                                      <td className="text-warning fw-medium">
                                        {formatCurrency(data.tender)}
                                      </td>
                                      <td style={{ minWidth: "150px" }}>
                                        <div className="d-flex align-items-center gap-2">
                                          <div
                                            className="rounded-pill flex-grow-1"
                                            style={{
                                              height: 8,
                                              background: "#f0f2f5",
                                            }}
                                          >
                                            <div
                                              className="rounded-pill h-100"
                                              style={{
                                                width: `${pct}%`,
                                                background:
                                                  "linear-gradient(135deg,#1a3c5e,#2d6a4f)",
                                                transition: "width 1s",
                                              }}
                                            ></div>
                                          </div>
                                          <span
                                            className="text-muted"
                                            style={{
                                              fontSize: "11px",
                                              minWidth: "32px",
                                            }}
                                          >
                                            {pct}%
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All Projects Table */}
              <div className="card border-0 rounded-4 shadow-sm">
                <div className="card-body p-4">
                  <h6 className="fw-bold mb-3" style={{ color: "#1a3c5e" }}>
                    <i className="bi bi-list-ul me-2 text-info"></i>
                    All Projects Detail
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle small mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="px-3 py-2 small text-muted fw-semibold">
                            #
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Project Name
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Year
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Status
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Estimated
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Tender
                          </th>
                          <th className="py-2 small text-muted fw-semibold">
                            Contractor
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(budgetStats.projects || []).map((p, idx) => {
                          const statusCol = {
                            Planned: "#6366f1",
                            Sanctioned: "#0ea5e9",
                            "In Progress": "#f97316",
                            Completed: "#22c55e",
                            "On Hold": "#ef4444",
                            Cancelled: "#6b7280",
                          };
                          const col = statusCol[p.status] || "#6b7280";
                          return (
                            <tr key={p.project_id}>
                              <td className="px-3 text-muted">{idx + 1}</td>
                              <td style={{ maxWidth: "200px" }}>
                                <div className="fw-medium text-truncate">
                                  {p.name}
                                </div>
                              </td>
                              <td className="text-muted">
                                {p.financial_year || "—"}
                              </td>
                              <td>
                                <span
                                  className="badge rounded-pill small"
                                  style={{
                                    background: `${col}22`,
                                    color: col,
                                    border: `1px solid ${col}44`,
                                  }}
                                >
                                  {p.status}
                                </span>
                              </td>
                              <td className="text-success fw-medium">
                                {p.estimated_amount
                                  ? formatCurrency(
                                      parseFloat(p.estimated_amount),
                                    )
                                  : "—"}
                              </td>
                              <td className="text-warning fw-medium">
                                {p.tender_amount
                                  ? formatCurrency(parseFloat(p.tender_amount))
                                  : "—"}
                              </td>
                              <td className="text-muted">
                                {p.contractor_name || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
    </div>
  );
};

export default Reports;
